/**
 * The callback queue — the page this app exists for.
 *
 * Structure is 8loop's list-page pattern (research/design-system.md §3.4):
 * PageHeader, then an ActionBar of filter pills, then a dense table, then a
 * PaginationBar; `resolveListEmptyState` decides pristine-empty (nothing exists
 * yet) from filtered-empty (a filter found nothing, so the controls stay).
 *
 * Two decisions worth knowing about:
 *
 * - **Filters live in the URL.** "Everything waiting on the Feedback agent's TCN
 *   channel" is a thing FDEs say to each other, and a URL can be pasted into Slack.
 * - **Times are the server's strings, not the browser's.** `scheduled_for_local` is
 *   already rendered in the account's timezone; re-deriving it here would show an
 *   Indian account's 09:00 as 04:30 to anyone in London and look authoritative
 *   doing it. The relative "in 2 hours" beside it is the only thing computed here.
 */

import { useCallback, useEffect, useMemo } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Bot, Clock, PhoneOutgoing, Radio, Tag } from "lucide-react";
import { AppShell } from "@/components/app-shell";
import { AccountSwitcher } from "@/components/account-switcher";
import { CallbackSheet } from "@/components/callback-sheet";
import { PauseControl } from "@/components/pause-control";
import { TableSkeleton } from "@/components/table-skeleton";
import { PageHeader } from "@/components/ui/page-header";
import { ActionBar, ActionBarButton, ActionBarSearch } from "@/components/ui/action-bar";
import { ActionBarSelect } from "@/components/ui/action-bar-select";
import {
  DateRangePicker,
  type DateRangePresetKey,
  type DateRangeValue,
} from "@/components/ui/date-range-picker";
import { DataTable, type ColumnDefWithAriaSort } from "@/components/ui/data-table";
import { DenseTableViewport } from "@/components/ui/dense-table-viewport";
import { EmptyState } from "@/components/ui/empty-state";
import { ErrorState } from "@/components/ui/error-state";
import { PaginationBar } from "@/components/ui/pagination-bar";
import { SortableTableHeader } from "@/components/ui/sortable-table-header";
import { Badge } from "@/components/ui/badge";
import { ariaSortFor } from "@/lib/table-sort";
import {
  errorMessage,
  type Callback,
  type QueueOrderBy,
  type QueueOrderDir,
} from "@/lib/api";
import { GROUPS, relativeTime, statusMeta, TONE_CLASS } from "@/lib/labels";
import {
  useAccountConfig,
  useCallbacks,
  useRouteResolution,
  type QueueFilters,
} from "@/lib/queries";
import {
  groupWindowsByDate,
  QUEUE_DATE_DEFAULT,
  resolveQueueDate,
} from "@/lib/queue-date";
import { TABLE_ROW_GROUP_CLASS } from "@/lib/table-styles";

const ALL = "__all__";
const DEFAULT_LIMIT = 50;

export function QueuePage() {
  const { accountId, callbackId } = useParams<{ accountId: string; callbackId?: string }>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const group = params.get("group") ?? "due";
  const agentId = params.get("agent") ?? ALL;
  const channelId = params.get("channel") ?? ALL;
  const outcome = params.get("outcome") ?? ALL;
  const search = params.get("q") ?? "";
  const page = Number(params.get("page") ?? "1");
  const limit = Number(params.get("limit") ?? DEFAULT_LIMIT);

  const { data: config } = useAccountConfig(accountId);
  const timezone = config?.account.timezone ?? "UTC";

  // Resolved before the filters so the window and the pill are the same two
  // instants — see lib/queue-date.ts for why only some tabs get one.
  const showDateFilter = groupWindowsByDate(group);
  const datePreset = params.get("date");
  const dateFrom = params.get("from");
  const dateTo = params.get("to");
  // Memoised on the raw params, not the `params` object: a rolling preset resolves
  // against `now`, so keying this on an object that gets a new identity on every
  // keystroke would shift the window by a few milliseconds each time and miss the
  // query cache on every character typed into the search box.
  const date = useMemo(
    () => resolveQueueDate({ group, preset: datePreset, from: dateFrom, to: dateTo, timezone }),
    [group, datePreset, dateFrom, dateTo, timezone],
  );

  /*
   * Sort is in the URL only once somebody clicks a header. Left out, the server
   * picks the direction the tab deserves — soonest-first while the work is still
   * ahead of us — and echoes back what it used. Re-deriving that rule here would
   * put the same decision in two files, and the day they disagree the arrow
   * points one way while the rows go the other.
   */
  const orderBy = params.get("order_by") as QueueOrderBy | null;
  const orderDir = params.get("order_dir") as QueueOrderDir | null;

  const filters: QueueFilters = useMemo(
    () => ({
      group: group === ALL ? null : (group as QueueFilters["group"]),
      agent_id: agentId === ALL ? null : agentId,
      channel_id: channelId === ALL ? null : channelId,
      outcome: outcome === ALL ? null : outcome,
      q: search || null,
      from: date.from,
      to: date.to,
      order_by: orderBy,
      order_dir: orderDir,
      limit,
      offset: (page - 1) * limit,
    }),
    [group, agentId, channelId, outcome, search, date.from, date.to, orderBy, orderDir, limit, page],
  );

  const { data, isPending, isError, error, refetch, isFetching } = useCallbacks(accountId, filters);
  /* Only for naming channels in the filter and the row subline — see `channelNames`. */
  const { data: routes } = useRouteResolution(accountId);

  useEffect(() => {
    document.title = config?.account.name
      ? `Callbacks · ${config.account.name}`
      : "Callbacks";
  }, [config?.account.name]);

  /** Writes one filter and resets paging — page 3 of a different filter is nobody's intent. */
  const setFilter = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || value === ALL || value === "") next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setParams(next, { replace: true });
  };

  const setPage = (nextPage: number) => {
    const next = new URLSearchParams(params);
    if (nextPage <= 1) next.delete("page");
    else next.set("page", String(nextPage));
    setParams(next, { replace: true });
  };

  /**
   * The date filter writes either a preset key or a pair of instants, never both —
   * `resolveQueueDate` would let a stale `from`/`to` win over a freshly picked
   * preset, so each writer clears the other's params.
   */
  const setDate = (next: { preset?: DateRangePresetKey | "all"; range?: DateRangeValue }) => {
    const p = new URLSearchParams(params);
    p.delete("date");
    p.delete("from");
    p.delete("to");
    if (next.range) {
      p.set("from", next.range.from.toISOString());
      p.set("to", next.range.to.toISOString());
    } else if (next.preset && next.preset !== QUEUE_DATE_DEFAULT) {
      // The default stays out of the URL, so the common case has a clean link.
      p.set("date", next.preset);
    }
    p.delete("page");
    setParams(p, { replace: true });
  };

  /**
   * Sorting toggles direction on the column already in force and otherwise
   * starts ascending. The "already in force" reading comes from the response,
   * not the URL, so the first click on an unsorted table flips the tab's own
   * default rather than appearing to do nothing.
   */
  const sortBy = data?.order_by;
  const sortDir = data?.order_dir;
  // Stable identity: the column defs are memoised on it, and a new function every
  // render would rebuild them every render and throw away any column the user had
  // dragged wider.
  const toggleSort = useCallback(
    (column: string) => {
      const next = new URLSearchParams(params);
      next.set("order_by", column);
      next.set("order_dir", sortBy === column && sortDir === "asc" ? "desc" : "asc");
      next.delete("page");
      setParams(next, { replace: true });
    },
    [params, sortBy, sortDir, setParams],
  );

  // The group tab is a view, not a filter — it always has a value, so it is not
  // counted here and "Clear all" never strips it. The date range counts only when
  // it has been changed from the default, or every history tab would arrive
  // already showing "1 filter · Clear all" for a filter nobody set.
  const otherFilterCount =
    (agentId !== ALL ? 1 : 0) + (channelId !== ALL ? 1 : 0) + (outcome !== ALL ? 1 : 0) + (search ? 1 : 0);
  const appliedFilterCount = otherFilterCount + (showDateFilter && !date.isDefault ? 1 : 0);

  const facets = data?.facets;
  const rows = data?.callbacks ?? [];

  const agentOptions = useMemo(
    () => [
      { value: ALL, label: "All agents" },
      ...(facets?.agents ?? []).map((a) => ({
        value: a.agent_id ?? "unknown",
        label: a.agent_name ?? `Agent ${a.agent_id ?? "unknown"}`,
        hint: `${a.n} callback${a.n === 1 ? "" : "s"}`,
      })),
    ],
    [facets?.agents],
  );

  /*
    Channel ids turned into channel names.
    "Channel 1732" is a database key, not something anybody recognises — and the app
    already knows the answer: the routing cache carries a label for every channel on
    the tenant. Read here rather than fetched specially, so a dead credential just
    means the ids stay bare instead of the filter breaking.
  */
  const channelNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of routes?.channels ?? []) {
      if (c.channel_label) m.set(c.channel_id, c.channel_label);
      else if (c.agent_name) m.set(c.channel_id, c.agent_name);
    }
    return m;
  }, [routes?.channels]);

  const channelOptions = useMemo(
    () => [
      { value: ALL, label: "All channels" },
      ...(facets?.channels ?? []).map((c) => ({
        value: c.channel_id ?? "unknown",
        label: !c.channel_id
          ? "No channel recorded"
          : (channelNames.get(c.channel_id) ?? `Channel ${c.channel_id}`),
        hint: `${c.n} callback${c.n === 1 ? "" : "s"}`,
      })),
    ],
    [facets?.channels, channelNames],
  );

  // Only the outcomes this account has actually recorded, with their counts — the server
  // builds the list from the rows now rather than from the app's eight-name vocabulary, so
  // nothing here can be picked that returns an empty table.
  const outcomeOptions = useMemo(
    () => [
      { value: ALL, label: "All outcomes" },
      ...(facets?.outcomes ?? []).map((o) => ({
        value: o.value,
        label: o.label,
        hint: `${o.n} callback${o.n === 1 ? "" : "s"}`,
      })),
    ],
    [facets?.outcomes],
  );

  const groupCount = (value: string) =>
    facets?.groups.find((g) => g.value === value)?.count ?? 0;

  // Pristine vs filtered: with a group tab always applied, "nothing exists" means
  // the whole account is empty — which the summary, not this page's rows, knows.
  const totalEverything = Object.values(data?.summary.byStatus ?? {}).reduce((a, b) => a + b, 0);
  const isPristineEmpty = rows.length === 0 && totalEverything === 0;
  const isFilteredEmpty = rows.length === 0 && !isPristineEmpty;

  // The tab is a view, not a filter — so an empty Due tab with nothing filtering means
  // nothing is due, and "Nothing matches these filters" sends the reader hunting for a
  // filter they never set. Landing on an empty Due tab is the normal case on a quiet
  // afternoon, and it was the first thing the page said.
  const currentGroup = GROUPS.find((g) => g.value === group);

  // How many rows of this tab the date window is holding back. See the note below
  // the table for why this is only computed when nothing else is filtering.
  const hiddenByDate =
    showDateFilter && (date.from || date.to) && otherFilterCount === 0 && data
      ? Math.max(0, groupCount(group) - data.total)
      : 0;

  const columns = useQueueColumns(sortBy, sortDir, toggleSort, {
    accountId,
    search: params.toString(),
    channelNames,
  });

  return (
    <AppShell active="queue">
      <div className="flex flex-col h-full w-full max-w-full">
        <PageHeader
          breadcrumb={[
            { label: "Accounts", href: "/accounts" },
            { label: config?.account.name ?? "Account" },
          ]}
          title="Callback queue"
          description={
            config
              ? `Times are ${config.account.timezone}. Calls only go out inside this account's calling hours; a callback asked for outside them moves to the next open hour.`
              : undefined
          }
          actionsSlot={
            <>
              {/* Hidden on a phone: the header row does not wrap, so badges there
                  would squeeze the title into a column of single words. */}
              {config?.dry_run && (
                <Badge variant="outline" className="hidden text-[11px] sm:inline-flex">
                  Dry run — no calls placed
                </Badge>
              )}
              {/* Replaces a read-only "Paused" badge: the badge told you the
                  state and left the only way to change it three clicks away on a
                  settings tab. */}
              {config && accountId && (
                <PauseControl
                  accountId={accountId}
                  config={config}
                  dueNow={data?.summary.dueNow ?? 0}
                  pending={groupCount("due") + groupCount("in_flight")}
                />
              )}
              <AccountSwitcher />
            </>
          }
        />

        <div className="flex min-h-0 flex-1 flex-col gap-4 px-6">
          {!isPristineEmpty && (
            <ActionBar
              appliedFilterCount={appliedFilterCount}
              onClearFilters={() => {
                // The tab and the sort survive: neither is a filter, and having
                // "Clear all" quietly re-order the table would be a second,
                // unasked-for change hiding inside the one that was asked for.
                const next = new URLSearchParams();
                if (group !== "due") next.set("group", group);
                if (orderBy) next.set("order_by", orderBy);
                if (orderDir) next.set("order_dir", orderDir);
                setParams(next, { replace: true });
              }}
              left={
                <>
                  <div className="flex flex-wrap items-center gap-1.5 pr-1">
                    {/*
                      These pills are the closest thing this page has to tabs, so
                      which one is selected has to survive a hover. The design
                      system's active treatment is `bg-accent` and its hover is also
                      `bg-accent`, so pointing at "Called" painted it exactly like
                      the selected "Due" beside it — two tabs looking current at once,
                      and the wrong one under your finger. Softening the inactive
                      hover makes it read as a preview instead of a selection; the
                      active pill keeps the full fill.
                    */}
                    {GROUPS.map((g) => (
                      <ActionBarButton
                        key={g.value}
                        active={group === g.value}
                        className={group === g.value ? undefined : "hover:bg-accent/40"}
                        title={g.hint}
                        onClick={() => setFilter("group", g.value)}
                      >
                        {g.label}
                        <span className="tabular-nums text-muted-foreground">
                          {groupCount(g.value)}
                        </span>
                      </ActionBarButton>
                    ))}
                  </div>
                  <ActionBarSelect
                    value={agentId}
                    onChange={(v) => setFilter("agent", v)}
                    options={agentOptions}
                    icon={Bot}
                    ariaLabel="Source agent"
                    placeholder="Agent"
                    clearValue={ALL}
                    searchable={agentOptions.length > 6}
                  />
                  <ActionBarSelect
                    value={channelId}
                    onChange={(v) => setFilter("channel", v)}
                    options={channelOptions}
                    icon={Radio}
                    ariaLabel="Source agent channel"
                    placeholder="Channel"
                    clearValue={ALL}
                    searchable={channelOptions.length > 6}
                  />
                  <ActionBarSelect
                    value={outcome}
                    onChange={(v) => setFilter("outcome", v)}
                    options={outcomeOptions}
                    icon={Tag}
                    ariaLabel="Outcome"
                    placeholder="Outcome"
                    clearValue={ALL}
                  />
                  {showDateFilter && (
                    <DateRangePicker
                      value={date.value}
                      timezone={timezone}
                      label="Date range"
                      /*
                        The ring classes this component declares live on its wrapper
                        <div>, which is never focusable, and the trigger button inside
                        carries a bare `outline-none`. So focus landed here invisibly.
                        `focus-within` is the wrapper-level equivalent and fires when
                        the inner button takes focus.
                      */
                      className="rounded-md focus-within:ring-[3px] focus-within:ring-ring/50"
                      // Only reachable by clearing, so the placeholder is what the
                      // cleared state means rather than a generic prompt.
                      placeholder="All time"
                      activePresetKey={date.presetKey}
                      onPresetApply={(key) => setDate({ preset: key })}
                      onChange={(range) => setDate(range ? { range } : { preset: "all" })}
                      onClear={() => setDate({ preset: "all" })}
                      // The escape hatch is named because the preset list has no
                      // "All time" row — the design system's does not define one,
                      // and the × is not somewhere you look for a date option.
                      footerHint={`On when a callback is due, in ${timezone}. Clear (×) for all time.`}
                    />
                  )}
                </>
              }
              right={
                <ActionBarSearch
                  value={search}
                  onValueChange={(v) => setFilter("q", v)}
                  onClear={() => setFilter("q", null)}
                  placeholder="Number or name"
                  /* The placeholder disappears as soon as anyone types. */
                  aria-label="Search callbacks by number or contact name"
                  pending={isFetching}
                />
              }
            />
          )}

          {/*
            A date filter that silently drops rows is the failure mode worth
            spending a line on: "Called 37" over nine rows is the moment somebody
            stops trusting the numbers on this page. The tab counts are
            deliberately account-wide, so the difference is exactly what the window
            is holding back — said out loud, directly under the count it explains,
            with the way out beside it.

            Only when no other filter is applied. Agent, channel, outcome and
            search all shrink `total` too, and blaming the date for their work
            would be a confident lie.
          */}
          {hiddenByDate > 0 && (
            <p className="-mt-1 text-xs text-muted-foreground">
              Showing {data?.total ?? 0} of {groupCount(group)} — {hiddenByDate} older{" "}
              {hiddenByDate === 1 ? "callback is" : "callbacks are"} outside this date range.{" "}
              <button
                type="button"
                className="cursor-pointer underline underline-offset-2 hover:text-foreground"
                onClick={() => setDate({ preset: "all" })}
              >
                Show all time
              </button>
            </p>
          )}

          <div className="min-h-0 flex-1">
            {isError ? (
              <ErrorState
                title="Couldn't load the queue"
                description={errorMessage(error)}
                onRetry={() => refetch()}
              />
            ) : isPending ? (
              <TableSkeleton columnCount={7} rowCount={8} />
            ) : isPristineEmpty ? (
              <EmptyState
                title="No callbacks yet"
                description="No agent on this account is posting finished calls here yet. Add the agent you want callbacks from under Configuration → Agents, then hand its brief to your Claude Code — that is what installs the post-call hook. The dry run on the same page shows what this app would do with a call before any of it is real."
                action={
                  <ActionBarButton
                    onClick={() => navigate(`/accounts/${accountId}/settings?tab=agents`)}
                  >
                    Open configuration
                  </ActionBarButton>
                }
              />
            ) : (
              <DenseTableViewport>
                <DataTable
                  columns={columns}
                  data={rows}
                  // Carry the filters into the URL of the open row. Without the
                  // search string the route falls back to the default group, so
                  // opening a row from Called dumped the table behind the sheet
                  // onto "Nothing matches these filters" and closing it lost the
                  // tab you were working in.
                  onRowClick={(row) =>
                    navigate({
                      pathname: `/accounts/${accountId}/queue/${row.id}`,
                      search: params.toString(),
                    })
                  }
                  /* Same missing cursor as the accounts table — see the note there. */
                  rowClassName={`${TABLE_ROW_GROUP_CLASS} cursor-pointer`}
                  rowHeightClassName="h-10"
                  headerHeightClassName="h-8"
                  stickyHeader
                  emptyContent={
                    isFilteredEmpty ? (
                      appliedFilterCount === 0 && hiddenByDate === 0 ? (
                        <EmptyState
                          size="compact"
                          title={`Nothing in ${currentGroup?.label ?? "this tab"}`}
                          description="The count on each tab above shows where this account's callbacks are."
                        />
                      ) : (
                        <EmptyState
                          size="compact"
                          title="Nothing matches these filters"
                          description="Remove a filter above, or switch to another tab — the counts on each tab show where the rows are."
                        />
                      )
                    ) : undefined
                  }
                />
              </DenseTableViewport>
            )}
          </div>

        </div>

        {!isPristineEmpty && !isError && !isPending && (
          <PaginationBar
            className="shrink-0"
            // The bar counts pages from zero; the URL counts from one, because
            // `?page=0` in a shared link reads like a bug.
            page={page - 1}
            limit={limit}
            total={data?.total ?? 0}
            onPageChange={(p) => setPage(p + 1)}
            onLimitChange={(l) => setFilter("limit", l === DEFAULT_LIMIT ? null : String(l))}
          />
        )}
      </div>

      {callbackId && (
        <CallbackSheet
          accountId={accountId!}
          callbackId={callbackId}
          onClose={() =>
            navigate(
              { pathname: `/accounts/${accountId}/queue`, search: params.toString() },
              { replace: true },
            )
          }
        />
      )}
    </AppShell>
  );
}

/**
 * Columns.
 *
 * "When" leads because the queue is a timeline: the only question a person opens
 * this table with is what is about to happen, or what just did. Status is stated as
 * coloured text rather than a badge, per §4.2 — badges here mean identifiers.
 *
 * **The widths have to add up to less than the table's container.** `.table-dense`
 * sets `table-layout: fixed`, and in fixed layout a column never shrinks below the
 * width it asked for — the table grows past its parent instead. It does not
 * disappear (`.table-container` scrolls both ways), but on macOS the scrollbar is
 * an overlay that only exists once you are already scrolling, so a column past the
 * right edge has no affordance at all. The first version of this table asked for
 * 1320px inside 1126px and read, convincingly, as a seven-column table: "Last
 * change" was simply not there. The second version budgeted from text widths alone
 * and truncated its own headers ("Tri…", "Last cha…"), because `.table-container th`
 * carries `padding: 15px` and `.table-dense` zeroes only the *vertical* half — every
 * column pays 30px it cannot use. These total 1091 with that included, and stretch to
 * fill whatever the container actually is: at 1440 with the rail open that is 1126, so
 * every column is on screen. Below roughly 1380 the table starts to scroll again —
 * collapsing the rail buys back about 180px, and the row sheet has every field
 * regardless.
 *
 * Only the two timestamp columns sort, because they are the only two the server
 * can order by, and offering a header that does nothing is worse than offering
 * none. Everything else people want to "sort by" here is really a filter, and the
 * pills above the table already do it.
 */
function useQueueColumns(
  orderBy: QueueOrderBy | undefined,
  orderDir: QueueOrderDir | undefined,
  onSort: (column: string) => void,
  /**
   * What the Number cell needs to be a real link, and what the From-agent cell
   * needs to name a channel. Grouped into one object so adding the next one does
   * not mean a fourth positional argument nobody can read at the call site.
   */
  ctx: { accountId: string | undefined; search: string; channelNames: Map<string, string> },
): ColumnDefWithAriaSort<Callback, unknown>[] {
  return useMemo(
    () => [
      {
        id: "when",
        header: () => (
          <SortableTableHeader
            label="When"
            column="when"
            orderBy={orderBy}
            orderDir={orderDir}
            onSort={onSort}
            // The base is `p-0`, so the click target is the glyph height of the
            // label — a ~16px strip inside a 40px header cell. The padding here
            // claims the cell's own height without moving the text.
            className="h-8 -mb-4 pb-4"
          />
        ),
        ariaSort: ariaSortFor("when", orderBy, orderDir),
        size: 120,
        cell: ({ row }) => {
          const c = row.original;
          if (!c.scheduled_for_local) {
            /*
              An em dash here reads as "no data", which is wrong — it means this
              callback was never given a time, and that is a fact with a cause. The
              cause is usually in `attention_reason` ("nobody could work out when
              'kal shaam' is"), so it goes in the tooltip rather than being lost.
            */
            return (
              <span className="text-muted-foreground" title={c.attention_reason ?? undefined}>
                never scheduled
              </span>
            );
          }
          return (
            <div className="flex min-w-0 flex-col justify-center leading-tight">
              <span className={c.overdue ? "text-warning" : undefined}>
                {c.scheduled_for_local}
              </span>
              <span className="text-[11px] text-muted-foreground">
                {c.overdue ? `due ${relativeTime(c.scheduled_for)}` : relativeTime(c.scheduled_for)}
              </span>
            </div>
          );
        },
      },
      {
        id: "number",
        header: "Number",
        size: 145,
        cell: ({ row }) => (
          <div className="flex min-w-0 flex-col justify-center leading-tight">
            {/*
              The number is a real <Link>, and that is the only keyboard path into a
              callback. The row's own onClick is on a bare <tr> with no tabIndex and
              no key handler, so before this every one of the fifty rows was
              mouse-only: tabbing went filter pills → search → pagination and skipped
              the table entirely, which put "Call now", "Cancel" and the reschedule
              field out of reach for anyone not using a mouse. `stopPropagation`
              stops the row handler navigating a second time on a plain click.
            */}
            <Link
              to={{
                pathname: `/accounts/${ctx.accountId}/queue/${row.original.id}`,
                search: ctx.search,
              }}
              onClick={(e) => e.stopPropagation()}
              className="rounded-sm font-mono text-xs hover:underline focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              {row.original.phone_display}
            </Link>
            {row.original.contact_name && (
              <span
                className="truncate text-[11px] text-muted-foreground"
                title={row.original.contact_name}
              >
                {row.original.contact_name}
              </span>
            )}
          </div>
        ),
      },
      {
        id: "outcome",
        header: "Why",
        size: 148,
        cell: ({ row }) => <span className="text-xs">{row.original.outcome_label}</span>,
      },
      {
        id: "source",
        header: "From agent",
        size: 174,
        cell: ({ row }) => {
          const c = row.original;
          return (
            <div className="flex min-w-0 flex-col justify-center leading-tight">
              {/*
                Truncated cells carry a `title`. `DataTable` puts a real tooltip on
                any cell whose value is a long string, but only when the cell *is*
                a string — a two-line cell like this one renders JSX, so it opts
                out of that and has to say the whole name some other way.
              */}
              <span className="truncate" title={c.source_agent_name ?? undefined}>
                {c.source_agent_name ?? "—"}
              </span>
              <span
                className="truncate text-[11px] text-muted-foreground"
                title={c.source_channel_id ? `channel ${c.source_channel_id}` : undefined}
              >
                {!c.source_channel_id
                  ? "no channel recorded"
                  : (ctx.channelNames.get(c.source_channel_id) ??
                    `channel ${c.source_channel_id}`)}
              </span>
            </div>
          );
        },
      },
      {
        id: "dial",
        header: "Dialling out on",
        size: 172,
        cell: ({ row }) => {
          const c = row.original;
          if (!c.dial_channel_label && !c.dial_routing_identifier) {
            // Nothing to dial by — not "nothing configured", which is the normal
            // state now that a callback goes back out on its own channel. This is
            // the one thing on the row that means "this will never be dialled",
            // so it says so rather than showing a dash.
            return <span className="text-destructive">no channel resolved</span>;
          }
          const label = c.dial_channel_label ?? c.dial_routing_identifier;
          return (
            <div className="flex min-w-0 items-center gap-1.5">
              <PhoneOutgoing className="size-3 shrink-0 text-muted-foreground" />
              <span className="truncate" title={label ?? undefined}>
                {label}
              </span>
            </div>
          );
        },
      },
      {
        id: "attempts",
        header: "Tries",
        size: 72,
        cell: ({ row }) => (
          <span className="tabular-nums text-muted-foreground">{row.original.attempts}</span>
        ),
      },
      {
        id: "status",
        header: "Status",
        size: 128,
        cell: ({ row }) => {
          const meta = statusMeta(row.original.status);
          return (
            <span className={`text-xs ${TONE_CLASS[meta.tone]}`} title={meta.hint}>
              {meta.label}
            </span>
          );
        },
      },
      {
        id: "updated",
        header: () => (
          <SortableTableHeader
            label="Last change"
            column="updated_at"
            orderBy={orderBy}
            orderDir={orderDir}
            onSort={onSort}
            // The base is `p-0`, so the click target is the glyph height of the
            // label — a ~16px strip inside a 40px header cell. The padding here
            // claims the cell's own height without moving the text.
            className="h-8 -mb-4 pb-4"
          />
        ),
        ariaSort: ariaSortFor("updated_at", orderBy, orderDir),
        size: 132,
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Clock className="size-3 shrink-0" />
            {row.original.updated_at_local}
          </span>
        ),
      },
    ],
    [orderBy, orderDir, onSort, ctx.accountId, ctx.search, ctx.channelNames],
  );
}
